import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router";
import {
  X,
  Mail,
  Phone,
  UserCircle2,
  CalendarDays,
  IdCard,
  Clock,
  Copy,
  ChevronRight,
  Briefcase,
  Monitor,
  Search,
  FileText,
  User,
  MailCheck,
  PhoneCall,
  MessageSquare,
  Tag,
  ShoppingBag,
  Package,
  Calendar,
  Plus,
  PlayCircle,
  FileTextIcon,
  TimerIcon,
  BadgeDollarSign,
  MessageCircleCodeIcon,
  PhoneCallIcon,
  MessageCircle,
} from "lucide-react";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";
import Chip from "../../../../../component/ui/chip";
import CommentsSection from "../../../../../component/comment";
import { Alert, Snackbar } from "@mui/material";
import InvoiceQuickView from "../../../../../component/invoice/InvoiceQuickView";
import EmployeeDirectoryAddUpdate from "../../../../organization/emplyee-directory/add-update";
import AddActivity from "../../../../../component/modals/add-activity";
import moment from "moment";

const tabs = [
  { key: "timeline", label: "Timeline", icon: <TimerIcon size={16} /> },
  { key: "summary", label: "Summary", icon: <User size={16} /> },
  // { key: "deal", label: "Deal", icon: <FileTextIcon size={16} /> },
  {
    key: "transactions",
    label: "Transactions",
    icon: <BadgeDollarSign size={16} />,
  },
  {
    key: "comments",
    label: "Comments",
    icon: <MessageCircleCodeIcon size={16} />,
  },
  { key: "call", label: "Calls", icon: <PhoneCallIcon size={16} /> },
  { key: "emails", label: "Emails", icon: <Mail size={16} /> },
  { key: "sms", label: "Sms", icon: <MessageCircle size={16} /> },
];

function classNames(...xs) {
  return xs.filter(Boolean).join(" ");
}

function Badge({ children, tone = "default", icon: Icon }) {
  const tones = {
    default: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-800",
    red: "bg-rose-100 text-rose-700",
    grey: "bg-neutral-100 text-neutral-700",
  };
  return (
    <span
      className={classNames(
        "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium",
        tones[tone],
      )}
    >
      {Icon ? <Icon size={14} /> : null}
      {children}
    </span>
  );
}

function Stat({ title, value, icon: Icon, hint, tone = "default" }) {
  return (
    <div className="group relative rounded-2xl border border-solid border-slate-200 bg-white p-4 shadow-card">
      <div className="flex items-start gap-3">
        <div className="min-w-0">
          <div className="text-xs text-slate-500 capitalize">
            {title.includes("_")
              ? title.split("_").map((word) => ` ${word}`)
              : title}
          </div>
          {/* truncate */}
          <div className="text-lg font-semibold tracking-tight">{value}</div>
          {hint && <div className="mt-1 text-xs text-slate-400">{hint}</div>}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  icon: Icon,
  copyable,
  link,
  endTag = false,
  LastElement,
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="grid grid-cols-12 items-center gap-2 rounded-xl border border-solid border-slate-200 bg-white p-3 transition hover:bg-slate-50 shadow-card">
      <div className="col-span-4 flex items-center gap-2 text-[13px] font-medium text-slate-500">
        {Icon ? <Icon className="size-4" /> : null}
        {label}
      </div>
      <div className="col-span-8 flex items-center justify-between gap-2 text-[13px] text-slate-800">
        {link ? (
          <a
            href={link}
            className="truncate underline decoration-slate-300 underline-offset-2 hover:text-blue-600 hover:decoration-blue-400"
          >
            {value}
          </a>
        ) : (
          <span className="truncate">{value}</span>
        )}
        {copyable || endTag ? (
          copyable ? (
            <button
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(String(value ?? ""));
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {}
              }}
              className="inline-flex items-center gap-1 rounded-md border border-solid border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-600 hover:bg-slate-100"
            >
              <Copy className="size-3.5" /> {copied ? "Copied" : "Copy"}
            </button>
          ) : (
            <LastElement />
          )
        ) : null}
      </div>
    </div>
  );
}

function normalizeToArray(value) {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

// const statusColor = {
//   pending: "bg-amber-50 text-amber-700 border border-amber-200",

//   in_progress: "bg-blue-50 text-blue-700 border border-blue-200",

//   revision: "bg-purple-50 text-purple-700 border border-purple-200",

//   on_hold: "bg-rose-50 text-rose-700 border border-rose-200",

//   completed: "bg-emerald-50 text-emerald-700 border border-emerald-200",
// };
const statusColor = {
  completed: { background: "#E6F9F0", color: "#0F9D58" }, // emerald green
  on_hold: { background: "#E8F0FE", color: "#1A73E8" }, // royal blue
  pending: { background: "#fff3e0", color: "#e65100" }, // deep orange
  in_progress: { background: "#F3E8FF", color: "#9333EA" }, // violet
  revision: { background: "#E0F7FA", color: "#00838F" }, // cyan / teal
  "no answer update 2": { background: "#f3f4f6", color: "#374151" }, // cyan / teal
  "wrong number": { background: "#fee2e2", color: "#b91c1c" }, // cyan / teal
  "call back": { background: "#fffbeb", color: "#78350f" }, // cyan / teal
  "not interested": { background: "#fee2e2", color: "#b91c1c" }, // cyan / teal
  "do not contact": { background: "#f9fafb", color: "#374151" }, // cyan / teal
  duplicate: { background: "#ede9fe", color: "#5b21b6" },
};
export default function ProjectQuickView({
  open = true,
  onClose = () => {},
  data,
}) {
  const [lead, setLead] = useState({});
  const [activeTab, setActiveTab] = useState("timeline");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState(lead?.leadstatus);
  const containerRef = useRef(null);
  const apiCall = useApiCall();
  const [isLoading, setIsLoading] = useState(false);

  const [invoicePopup, setInvoicePopup] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);

  const [contact, setcontact] = useState({});
  const [alertData, setAlertData] = useState({ isShow: false });
  const [changeModal, setChangeModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [usersList, setUsers] = useState([]);

  const GetData = async (key) => {
    let apiData = JSON.stringify({
      projectkey: key,
    });

    try {
      let call = await apiCall({
        method: "get",
        url: `projects/get-project/${key}`,
        data: apiData,
      });
      let projectData = call?.project;

      console.log("ProjectData", projectData);
      setIsLoading(true);

      let activity = projectData?.recent_activity?.map((item) => {
        const dateObj = new Date(item.time);
        const formattedDate = dateObj
          .toISOString()
          .slice(0, 16)
          .replace("T", " ");
        // This gives "YYYY-MM-DD HH:MM"

        return {
          ts: formattedDate,
          text: item.data,
        };
      });

      const sdata = {
        projectkey: projectData?.projectkey,
        id: projectData?.projectid,
        invoiceId: projectData?.invoiceid,
        status: projectData?.status,
        payment: projectData?.payment,
        cost: projectData?.projectcost,
        start: projectData?.startdate,
        end: projectData?.enddate,
        brief: projectData?.projectbrief,
        rush: projectData?.rush,
        createdAt: projectData?.created_at,
        updatedAt: projectData?.updated_at,

        activities: projectData?.activities,

        customer: projectData?.customer_details,
        agent: projectData?.agent_details,
        support: projectData?.support_details,
        transactions: projectData?.transactions,
        comments: projectData?.comments,
        stats: projectData?.stats ?? [],
        invoice_details: projectData?.invoice_details ?? [],
        project_deals: projectData?.project_deals ?? [],
        package_details: projectData?.package_details ?? [],
      };

      setStatus(projectData?.status);
      setLead(sdata);
      setIsLoading(true);
    } catch (error) {
      console.warn("Error : ", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await apiCall({
        method: "get",
        url: `users/agents-list`,
      });

      console.log("Users API Response ===>", res);
      const newUsers = res?.agents || res?.customers || res?.data || [];
      setUsers(newUsers);
    } catch (err) {
      console.error("Error fetching users", err);
    } finally {
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // useOnClickOutside(containerRef, () => onClose());

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
      if (e.key.toLowerCase() === "f" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        const el = document.getElementById("quickview-search");
        el?.focus();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(lead?.projectkey);
    setChangeModal(false);
  };

  const onActivitySuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    GetData(lead?.projectkey);
    setShowActivityModal(false);
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };
  const handleClose = () => {
    setAlertData({ isShow: false });
  };

  useEffect(() => {
    GetData(data?.project_key || data?.projectkey);
  }, [data]);

  const activityConfig = {
    call: {
      icon: PhoneCall,
      color: "#6366F1", // Indigo
    },
    email: {
      icon: Mail,
      color: "#0EA5E9", // Sky Blue
    },
    meeting: {
      icon: Calendar,
      color: "#10B981", // Green
    },
    note: {
      icon: FileText,
      color: "#F59E0B", // Amber
    },
  };

  const T = {
    bg: "#EDE8E1",
    sidebar: "#1a1726",
    sidebarText: "rgba(255,255,255,0.45)",
    sidebarActive: "#FFFFFF",
    sidebarActiveBg: "rgba(99,102,241,0.15)",
    sidebarHover: "rgba(255,255,255,0.05)",
    sidebarAccent: "#818cf8",
    card: "#FFFFFF",
    cardBorder: "rgba(0,0,0,0.10)",
    cardHover: "#FAF8F6",
    text: "#1a1520",
    textSecondary: "#5c5466",
    textMuted: "#908892",
    border: "rgba(0,0,0,0.08)",
    accent: "#6366f1",
    accentLight: "#EEF0FF",
    accentText: "#4F46E5",
    inputBg: "#F3EFE9",
    inputBorder: "rgba(0,0,0,0.1)",
    modalBg: "#FFFFFF",
    rowHover: "#F5F1EC",
    selectedRow: "#EEEAFF",
    selectedBorder: "#6366f1",
    shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
    shadowLg: "0 8px 30px rgba(0,0,0,0.08)",
  };

  const css = {
    app: {
      display: "flex",
      height: "100vh",
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      background: T.bg,
      color: T.text,
      overflow: "hidden",
    },
    sidebar: {
      width: 250,
      background: T.sidebar,
      display: "flex",
      flexDirection: "column",
      flexShrink: 0,
      overflow: "hidden",
    },
    main: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    card: {
      background: T.card,
      border: `1px solid ${T.cardBorder}`,
      borderRadius: 12,
      padding: 16,
      boxShadow: T.shadow,
    },
    btn: {
      padding: "7px 14px",
      borderRadius: 8,
      border: `1px solid ${T.border}`,
      background: T.card,
      color: T.textSecondary,
      cursor: "pointer",
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      transition: "all 0.15s",
      boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
    },
    btnPrimary: {
      padding: "8px 18px",
      borderRadius: 8,
      border: "none",
      background: "linear-gradient(92deg,#0057FB 1.54%,#003495 114.12%)",
      color: "#fff",
      cursor: "pointer",
      fontSize: 13,
      fontWeight: 600,
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontFamily: "inherit",
      boxShadow: "0 2px 10px rgba(99,102,241,0.3)",
    },
    input: {
      background: T.inputBg,
      border: `1px solid ${T.inputBorder}`,
      borderRadius: 8,
      padding: "9px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      fontFamily: "inherit",
      width: "100%",
    },
    select: {
      background: T.inputBg,
      border: `1px solid ${T.inputBorder}`,
      borderRadius: 8,
      padding: "6px 12px",
      color: T.text,
      fontSize: 13,
      outline: "none",
      fontFamily: "inherit",
      cursor: "pointer",
    },
    badge: (color) => ({
      display: "inline-flex",
      alignItems: "center",
      padding: "3px 9px",
      borderRadius: 6,
      fontSize: 11,
      fontWeight: 600,
      background: color + "14",
      color: color,
      letterSpacing: "0.3px",
    }),
    pill: (active) => ({
      padding: "5px 12px",
      borderRadius: 20,
      fontSize: 12,
      fontWeight: active ? 600 : 400,
      border: active
        ? "1px solid rgba(99,102,241,0.35)"
        : `1px solid ${T.border}`,
      background: active ? T.accentLight : T.card,
      color: active ? T.accentText : T.textMuted,
      cursor: "pointer",
      fontFamily: "inherit",
      boxShadow: active ? "none" : "0 1px 2px rgba(0,0,0,0.03)",
    }),
  };

  const STATUS_COLORS = {
    NEW: "#6366f1",
    Contacted: "#3b82f6",
    "Callback Scheduled": "#8b5cf6",
    Interested: "#059669",
    "Proposal Sent": "#d97706",
    Negotiating: "#ea580c",
    Won: "#16a34a",
    Lost: "#dc2626",
    "No Answer": "#6b7280",
    Active: "#9ca3af",
    "Not Interested": "#dc2626",
    ATT: "#64748b",
  };

  const HEALTH_COLORS = {
    Excellent: "#059669",
    Good: "#16a34a",
    Fair: "#d97706",
    "At Risk": "#ea580c",
    Critical: "#dc2626",
  };

  const activityIcon = (type) =>
    ({
      call: "phone",
      email: "mail",
      sms: "msg",
      meeting: "users",
      note: "edit",
      voicemail: "mic",
    })[type] || "check";
  const activityColor = (type) =>
    ({
      call: "#6366f1",
      email: "#3b82f6",
      sms: "#059669",
      message: "#059669",
      chat: "#059669",
      meeting: "#d97706",
      note: "#8b5cf6",
      voicemail: "#ea580c",
    })[type] || "#6b7280";

  ////////////// fake data for calls //////////////
  const refreshLead = {
    id: lead?.id,
    leadName: "Collins",
    phone: lead?.customer?.phone,
    activities: [],
  };

  const I = (name, size = 16) => (icons[name] ? icons[name](size) : null);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  const fmtTime = (iso) =>
    new Date(iso).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  const fmtDateTime = (iso) => `${fmtDate(iso)} ${fmtTime(iso)}`;

  const icons = {
    search: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
      </svg>
    ),
    check: (s = 16) => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-check-check-icon lucide-check-check"
      >
        <path d="M18 6 7 17l-5-5" />
        <path d="m22 10-7.5 7.5L13 16" />
      </svg>
    ),
    phone: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
      </svg>
    ),
    mail: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    ),
    msg: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22z" />
      </svg>
    ),
    user: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
      </svg>
    ),
    users: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    plus: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M5 12h14" />
        <path d="M12 5v14" />
      </svg>
    ),
    clock: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    dollar: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <line x1="12" x2="12" y1="2" y2="22" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    chart: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M3 3v18h18" />
        <path d="m19 9-5 5-4-4-3 3" />
      </svg>
    ),
    bell: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
    ),
    chevDown: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    ),
    chevRight: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    ),
    x: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </svg>
    ),
    check: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M20 6 9 17l-5-5" />
      </svg>
    ),
    edit: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      </svg>
    ),
    trash: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M3 6h18" />
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      </svg>
    ),
    file: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
        <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      </svg>
    ),
    star: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth="1"
      >
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    filter: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
      </svg>
    ),
    grid: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <rect width="7" height="7" x="3" y="3" rx="1" />
        <rect width="7" height="7" x="14" y="3" rx="1" />
        <rect width="7" height="7" x="14" y="14" rx="1" />
        <rect width="7" height="7" x="3" y="14" rx="1" />
      </svg>
    ),
    list: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <line x1="8" x2="21" y1="6" y2="6" />
        <line x1="8" x2="21" y1="12" y2="12" />
        <line x1="8" x2="21" y1="18" y2="18" />
        <line x1="3" x2="3.01" y1="6" y2="6" />
        <line x1="3" x2="3.01" y1="12" y2="12" />
        <line x1="3" x2="3.01" y1="18" y2="18" />
      </svg>
    ),
    target: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="6" />
        <circle cx="12" cy="12" r="2" />
      </svg>
    ),
    refresh: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
        <path d="M21 3v5h-5" />
        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
        <path d="M8 16H3v5" />
      </svg>
    ),
    tag: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
        <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
      </svg>
    ),
    play: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
      >
        <polygon points="6 3 20 12 6 21 6 3" />
      </svg>
    ),
    pause: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="none"
      >
        <rect x="6" y="4" width="4" height="16" />
        <rect x="14" y="4" width="4" height="16" />
      </svg>
    ),
    mic: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" x2="12" y1="19" y2="22" />
      </svg>
    ),
    send: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="m22 2-7 20-4-9-9-4Z" />
        <path d="m22 2-11 11" />
      </svg>
    ),
    home: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <polyline points="9 22 9 12 15 12 15 22" />
      </svg>
    ),
    settings: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
    arrowUp: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="m5 12 7-7 7 7" />
        <path d="M12 19V5" />
      </svg>
    ),
    arrowDown: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M12 5v14" />
        <path d="m19 12-7 7-7-7" />
      </svg>
    ),
    calendar: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <rect width="18" height="18" x="3" y="4" rx="2" />
        <line x1="16" x2="16" y1="2" y2="6" />
        <line x1="8" x2="8" y1="2" y2="6" />
        <line x1="3" x2="21" y1="10" y2="10" />
      </svg>
    ),
    link: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </svg>
    ),
    box: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    ),
    shield: (s = 16) => (
      <svg
        width={s}
        height={s}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
      </svg>
    ),
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 !pr-0 z-[60] flex items-stretch justify-end bg-slate-900/40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.aside
              ref={containerRef}
              initial={{ x: 40, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 40, opacity: 0 }}
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
              className="relative mt-0 mr-0 mb-auto ml-0 flex h-full w-[810px] max-w-[98vw] flex-col overflow-hidden rounded-3xl rounded-r-none  border border-solid border-slate-200 bg-[#ffffff] shadow-xl"
              aria-modal
              role="dialog"
            >
              {isLoading ? (
                <>
                  {/* Header */}
                  <div className=" bg-white/90 p-4 backdrop-blur supports-[backdrop-filter]:bg-white/70 w-full">
                    <div className="flex flex-wrap items-center justify-between gap-3 w-full">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex items-center justify-center size-12  bg-gradient-to-br from-indigo-500/20 to-emerald-500/20 rounded-full overflow-hidden">
                          <p className="capitalize font-semi-bold text-base text-black">
                            {lead?.customer?.name
                              ?.split(" ")
                              ?.map((n, i) => n.charAt(0))}
                          </p>
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h2 className="text-xl font-semibold text-slate-900">
                              {lead?.customer?.name}
                            </h2>

                            <div
                              className={`flex items-center gap-2 px-[9px] py-[3px] text-[11px] rounded-[6px] cursor-pointer transition-all duration-200 
                                `}
                              style={
                                statusColor[
                                  status?.toLowerCase()
                                ] || {
                                  background: "#f5f5f5",
                                  color: "#616161",
                                }
                              }
                              // ${statusColor[status] || "bg-gray-100 text-gray-600 border border-gray-200"}
                            >
                              {status
                                ? status
                                    ?.split("_")
                                    .map(
                                      (word) =>
                                        word.charAt(0).toUpperCase() +
                                        word.slice(1),
                                    )
                                    .join(" ")
                                : null}
                            </div>
                          </div>

                          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                            <span className="inline-flex items-center gap-1">
                              <MailCheck className="size-4" />{" "}
                              {lead?.customer?.email}
                            </span>

                            <span className="inline-flex items-center gap-1">
                              <PhoneCall className="size-4" />{" "}
                              {lead?.customer?.phone}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1.5 rounded-xl bg-[#0B54FF] px-3 py-2 text-sm font-medium text-white hover:bg-[#0849e0]"
                          onClick={onClose}
                          aria-label="Close"
                        >
                          <X className="size-4" /> Exit
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Body */}
                  <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden p-4 pt-0 w-full">
                    {/* Left: main content */}
                    <div className="col-span-12 flex min-h-0 flex-col gap-4">
                      {/* Top stats */}

                      <div
                        style={{
                          display: "flex",
                          gap: 10,
                          // marginTop: 14,
                          flexWrap: "wrap",
                        }}
                      >
                        {lead?.stats?.map((ele, idx) => (
                          <div
                            style={{
                              padding: "8px 14px",
                              background: "#F3EFE9",
                              borderRadius: 10,
                              border: `1px solid rgba(0,0,0,0.08)`,
                            }}
                          >
                            <div style={{ fontSize: 10, color: "#908892" }}>
                              {ele?.key?.includes("_")
                                ? ele?.key?.split("_").map((word) => ` ${word}`)
                                : ele?.key}
                            </div>
                            <div
                              style={{
                                fontSize: 15,
                                fontWeight: 700,
                                marginTop: 3,
                                color: "#1a1520",
                              }}
                            >
                              {ele?.value}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1.5 tracking-normal rounded-lg bg-[linear-gradient(92deg,#0057FB_1.54%,#003495_114.12%)] 
                          hover:bg-[linear-gradient(92deg,#0066FF_1.54%,#0045AA_114.12%)] px-[18px] py-2 text-[13px] font-semibold text-white"
                          onClick={() => setShowActivityModal(true)}
                          aria-label="Close"
                        >
                          <Plus className="size-[14px]" /> Add Follow Up
                        </button>

                        <button
                          className="inline-flex items-center gap-1.5 rounded-lg border border-solid border-[rgba(0,0,0,0.1)] bg-[rgb(243,239,233)]  px-[18px] py-2 text-[13px] font-semibold text-[rgb(26,21,32)] hover:bg-slate-50"
                          onClick={() => {
                            if (lead?.invoice_details) {
                              setInvoicePopup(true);
                              setInvoiceData(lead?.invoice_details);
                            }
                          }}
                          aria-label="Close"
                        >
                          <FileText className="size-[14px]" /> View Invoice
                        </button>
                      </div>

                      {/* Tabs */}
                      <div className="border-y border-solid pt-1 border-slate-200 flex items-center justify-between gap-2">
                        <div className="flex flex-wrap items-center ">
                          {tabs?.map((t) => (
                            <button
                              key={t.key}
                              onClick={() => setActiveTab(t.key)}
                              className={classNames(
                                "flex items-center gap-[6px] px-[16px] py-[11px] text-[13px] font-normal border-b-2 transition-all duration-200",
                                activeTab === t.key
                                  ? "border-blue-600 text-blue-600 font-semibold"
                                  : "border-transparent text-slate-500 hover:text-slate-800",
                              )}
                            >
                              {t.icon}
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="h-full overflow-auto rounded-2xl py-2">
                        {/* {activeTab === "timeline" && (
                          <div className="px-2 py-4 w-full h-full rounded-md">
                            {lead?.activities?.map((act, i) => {
                              const config =
                                activityConfig[act.type] || activityConfig.note;
                              const Icon = config.icon;
                              const color = config.color;
                              return (
                                <div
                                  key={act.id}
                                  style={{
                                    display: "flex",
                                    gap: 12,
                                    marginBottom: 20,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: "50%",
                                        background: color + "15",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: color,
                                        flexShrink: 0,
                                      }}
                                    >
                                      <Icon className="size-4" />
                                    </div>
                                    {i < lead.activities.length - 1 && (
                                      <div
                                        style={{
                                          width: 1,
                                          flex: 1,
                                          background: "rgba(0,0,0,0.08)",
                                          marginTop: 4,
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div style={{ flex: 1, paddingBottom: 4 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: "#1a1520",
                                        }}
                                      >
                                        {act.note}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: "#908892",
                                        }}
                                      >
                                        {act.subject}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: "#908892",
                                        marginBottom: 4,
                                      }}
                                    >
                                      {act.type.charAt(0).toUpperCase() +
                                        act.type.slice(1)}{" "}
                                      · {act.assignee?.name} · {act.due_time}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: "#5c5466",
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {act.details}
                                    </div>
                                    {act.hasRecording && (
                                      <div
                                        style={{
                                          marginTop: 6,
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          padding: "6px 12px",
                                          background: "#EEF0FF",
                                          borderRadius: 8,
                                          width: "fit-content",
                                        }}
                                      >
                                        <button
                                          style={{
                                            background: "none",
                                            border: "none",
                                            color: "#6366f1",
                                            cursor: "pointer",
                                            display: "flex",
                                          }}
                                        >
                                          <PlayCircle />
                                        </button>
                                        <div
                                          style={{
                                            width: 120,
                                            height: 3,
                                            background: "rgba(0,0,0,0.08)",
                                            borderRadius: 2,
                                          }}
                                        >
                                          <div
                                            style={{
                                              width: "0%",
                                              height: "100%",
                                              background: "#6366f1",
                                              borderRadius: 2,
                                            }}
                                          />
                                        </div>
                                        <span
                                          style={{
                                            fontSize: 10,
                                            color: "#908892",
                                          }}
                                        >
                                          0:00
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                            {lead?.activities?.length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  color: "#908892",
                                  padding: 40,
                                }}
                              >
                                No activities logged yet
                              </div>
                            )}
                          </div>
                        )} */}

                        {activeTab === "timeline" && (
                          <div className="px-2 w-full h-full rounded-md">
                            {lead?.activities?.map((act, i) => {
                              const config =
                                activityConfig[act.type] || activityConfig.note;
                              return (
                                <div
                                  key={act.id}
                                  style={{
                                    display: "flex",
                                    gap: 12,
                                    marginBottom: 16,
                                  }}
                                >
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: "50%",
                                        background:
                                          activityColor(
                                            act?.type?.toLowerCase(),
                                          ) + "12",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: activityColor(
                                          act?.type?.toLowerCase(),
                                        ),
                                        flexShrink: 0,
                                      }}
                                    >
                                      {I(
                                        activityIcon(act?.type?.toLowerCase()),
                                        15,
                                      )}
                                    </div>
                                    {i < lead.activities.length - 1 && (
                                      <div
                                        style={{
                                          width: 1,
                                          flex: 1,
                                          background: T.border,
                                          marginTop: 4,
                                        }}
                                      />
                                    )}
                                  </div>
                                  <div style={{ flex: 1, paddingBottom: 4 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: T.text,
                                        }}
                                        className="capitalize"
                                      >
                                        {act?.logcat}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: T.textMuted,
                                        }}
                                      >
                                        {act?.logtime}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textMuted,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {act?.logtype} · {act?.user?.name} ·{" "}
                                      {moment(
                                        act?.logtime,
                                        "DD-MMM-YYYY hh:mm:ss A",
                                      ).fromNow()}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textSecondary,
                                        lineHeight: 1.6,
                                      }}
                                    >
                                      {act.log}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                            {lead?.activities?.length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  color: "#908892",
                                  padding: 40,
                                }}
                              >
                                No activities logged yet
                              </div>
                            )}
                          </div>
                        )}

                        {activeTab === "summary" && (
                          <div
                            style={{
                              display: "flex",
                              // flexDirection: "column",
                              gap: 16,
                            }}
                          >
                            {/* Project Information */}
                            <div style={css.card} className="flex-1">
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Project Information
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "120px 1fr",
                                  gap: 10,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: T.textMuted }}>
                                  Customer Name:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.customer?.name || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Email:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.customer?.email || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Phone:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.customer?.phone || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Invoice ID:
                                </span>
                                <span
                                  style={{ color: T.text, fontWeight: 500 }}
                                >
                                  {lead?.invoiceId || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Cost:
                                </span>
                                <span style={{ color: T.text }}>
                                  {`$${lead?.cost} - ${lead?.payment}` || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Agent:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.agent?.name || "Not Assigned"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Support:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.support?.name || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Start Date:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.start ?? "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  End Date:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.end ?? "-"}
                                </span>
                              </div>
                            </div>

                            {/* Deal Information */}
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Deal Information
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "120px 1fr",
                                  gap: 10,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: T.textMuted }}>
                                  Deal Id:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.dealid || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Name:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.name || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Product:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.product?.products ??
                                    "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Package:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.package_details?.packagename ?? "-"}
                                </span>

                                <span style={{ color: T.textMuted }}>
                                  Time:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.dealtime ?? "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* {activeTab === "deal" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 16,
                            }}
                          >
                            
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Deal Information
                              </div>
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "120px 1fr",
                                  gap: 10,
                                  fontSize: 13,
                                }}
                              >
                                <span style={{ color: T.textMuted }}>
                                  Deal Id:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.dealid || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Name:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.name || "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Product:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.product?.products ??
                                    "-"}
                                </span>
                                <span style={{ color: T.textMuted }}>
                                  Package:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.package_details?.packagename ?? "-"}
                                </span>

                                <span style={{ color: T.textMuted }}>
                                  Time:
                                </span>
                                <span style={{ color: T.text }}>
                                  {lead?.project_deals?.dealtime ?? "-"}
                                </span>
                              </div>
                            </div>
                          </div>
                        )} */}

                        {activeTab === "transactions" && (
                          <div className="space-y-3">
                            {lead?.transactions?.map((txn) => (
                              <Field
                                key={txn?.transactionid}
                                label={txn?.transactiontime}
                                value={`$${txn?.amount}`}
                                icon={Briefcase}
                                // label2={deal?.amount}
                                endTag={true}
                                LastElement={() => (
                                  <Chip
                                    color={
                                      txn.transactionstatus == "success"
                                        ? "green"
                                        : "red"
                                    }
                                  >
                                    {txn.transactionstatus}
                                  </Chip>
                                )}
                              />
                            ))}
                          </div>
                        )}

                        {activeTab === "comments" && (
                          <>
                            <CommentsSection
                              type="project"
                              id={lead?.id}
                              apiBaseUrl="project"
                              setAlertData={setAlertData}
                            />
                          </>
                        )}

                        {activeTab === "call" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Make a Call
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  gap: 8,
                                  marginBottom: 16,
                                }}
                              >
                                <input
                                  style={{ ...css.input, flex: 1 }}
                                  value={refreshLead.phone}
                                  readOnly
                                />
                                <button style={css.btnPrimary}>
                                  {I("phone", 14)} Call Now
                                </button>
                              </div>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              Call History
                            </div>
                            {refreshLead.activities
                              .filter(
                                (a) =>
                                  a.type === "call" || a.type === "voicemail",
                              )
                              .map((call) => (
                                <div
                                  key={call.id}
                                  style={{
                                    ...css.card,
                                    display: "flex",
                                    gap: 12,
                                  }}
                                >
                                  <div
                                    style={{
                                      width: 34,
                                      height: 34,
                                      borderRadius: "50%",
                                      background:
                                        activityColor(call.type) + "12",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      color: activityColor(call.type),
                                      flexShrink: 0,
                                    }}
                                  >
                                    {I(activityIcon(call.type), 15)}
                                  </div>
                                  <div style={{ flex: 1 }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        marginBottom: 2,
                                      }}
                                    >
                                      <span
                                        style={{
                                          fontSize: 13,
                                          fontWeight: 600,
                                          color: T.text,
                                        }}
                                      >
                                        {call.summary}
                                      </span>
                                      <span
                                        style={{
                                          fontSize: 11,
                                          color: T.textMuted,
                                        }}
                                      >
                                        {fmtDateTime(call.date)}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textMuted,
                                        marginBottom: 4,
                                      }}
                                    >
                                      {call.agent} · {call.duration}
                                    </div>
                                    <div
                                      style={{
                                        fontSize: 12,
                                        color: T.textSecondary,
                                      }}
                                    >
                                      {call.details}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) =>
                                a.type === "call" || a.type === "voicemail",
                            ).length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  color: T.textMuted,
                                  padding: 40,
                                }}
                              >
                                No call history
                              </div>
                            )}
                          </div>
                        )}

                        {activeTab === "emails" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Compose Email
                              </div>
                              <input
                                placeholder="Subject..."
                                style={{ ...css.input, marginBottom: 8 }}
                              />
                              <textarea
                                placeholder="Type your email message..."
                                style={{
                                  ...css.input,
                                  minHeight: 120,
                                  resize: "vertical",
                                  marginBottom: 8,
                                }}
                              />
                              <button style={css.btnPrimary}>
                                {I("send", 14)} Send Email
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              Email History
                            </div>
                            {refreshLead.activities
                              .filter((a) => a.type === "email")
                              .map((email) => (
                                <div key={email.id} style={{ ...css.card }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: T.text,
                                      }}
                                    >
                                      {email.summary}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: T.textMuted,
                                      }}
                                    >
                                      {fmtDateTime(email.date)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: T.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    From: {email.agent}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: T.textSecondary,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {email.details}
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) => a.type === "email",
                            ).length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  color: T.textMuted,
                                  padding: 40,
                                }}
                              >
                                No email history
                              </div>
                            )}
                          </div>
                        )}

                        {activeTab === "sms" && (
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 12,
                            }}
                          >
                            <div style={css.card}>
                              <div
                                style={{
                                  fontSize: 14,
                                  fontWeight: 600,
                                  color: T.text,
                                  marginBottom: 12,
                                }}
                              >
                                Send SMS
                              </div>
                              <textarea
                                placeholder="Type your message..."
                                style={{
                                  ...css.input,
                                  minHeight: 80,
                                  resize: "vertical",
                                  marginBottom: 8,
                                }}
                              />
                              <button style={css.btnPrimary}>
                                {I("send", 14)} Send SMS
                              </button>
                            </div>
                            <div
                              style={{
                                fontSize: 14,
                                fontWeight: 600,
                                color: T.text,
                                marginBottom: 8,
                              }}
                            >
                              SMS History
                            </div>
                            {refreshLead.activities
                              .filter((a) => a.type === "sms")
                              .map((sms) => (
                                <div key={sms.id} style={{ ...css.card }}>
                                  <div
                                    style={{
                                      display: "flex",
                                      justifyContent: "space-between",
                                      marginBottom: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: T.text,
                                      }}
                                    >
                                      {sms.summary}
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 11,
                                        color: T.textMuted,
                                      }}
                                    >
                                      {fmtDateTime(sms.date)}
                                    </span>
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: T.textMuted,
                                      marginBottom: 4,
                                    }}
                                  >
                                    {sms.agent}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: 13,
                                      color: T.textSecondary,
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {sms.details}
                                  </div>
                                </div>
                              ))}
                            {refreshLead.activities.filter(
                              (a) => a.type === "sms",
                            ).length === 0 && (
                              <div
                                style={{
                                  textAlign: "center",
                                  color: T.textMuted,
                                  padding: 40,
                                }}
                              >
                                No SMS history
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between gap-2 border-t border-solid border-slate-200 bg-white p-3 w-full">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <IdCard className="size-4" /> ID {lead?.id}
                    </div>
                  </div>
                </>
              ) : (
                <InnerLoader wrapperClass={"w-full"} />
              )}
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {invoicePopup && invoiceData && (
        <InvoiceQuickView
          onClose={() => {
            setInvoicePopup(false);
            setInvoiceData(null);
          }}
          data={invoiceData}
          type="project"
        />
      )}

      {showActivityModal && (
        // <EmployeeDirectoryAddUpdate
        //   onClose={() => {
        //     setShowActivityModal(false);
        //   }}
        //   data={null}
        //   tabIndex={2}
        //   onError={onError}
        //   onSubmit={onSuccess}
        // id={lead?.id}
        // entitytype="project"
        // entityname={lead?.customer?.name}
        // />

        <AddActivity
          onSubmit={onActivitySuccess}
          onClose={() => {
            setShowActivityModal(false);
          }}
          onError={onError}
          UsersList={usersList}
          CustomerData={null}
          id={lead?.id}
          entitytype="project"
          entityname={lead?.customer?.name}
        />
      )}

      <Snackbar
        open={alertData?.isShow}
        autoHideDuration={3000}
        onClose={handleClose}
      >
        <Alert
          onClose={handleClose}
          severity={alertData?.type}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {alertData?.message}
        </Alert>
      </Snackbar>
    </>
  );
}

function QuickAction({ icon: Icon, label, href, onClick }) {
  const className =
    "inline-flex items-center gap-1.5 rounded-xl border border-solid border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
  if (href)
    return (
      <a className={className} href={href}>
        <Icon className="size-4" /> {label}
      </a>
    );
  return (
    <button className={className} onClick={onClick}>
      <Icon className="size-4" /> {label}
    </button>
  );
}

function TabPlaceholder({ icon: Icon, title, message }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-slate-300 rounded-2xl bg-white">
      <div className="flex items-center justify-center size-14 rounded-full bg-slate-100 mb-4">
        <Icon className="size-6 text-slate-500" />
      </div>
      <h3 className="text-lg font-semibold text-slate-700">{title}</h3>
      <p className="text-sm text-slate-500 mt-1">{message}</p>
      <div className="mt-4 px-4 py-2 text-xs text-slate-400 border border-solid border-slate-200 rounded-full bg-slate-50">
        🚧 Under Development
      </div>
    </div>
  );
}

function Activity({ items, compact = false }) {
  return (
    <ul className="space-y-3">
      {items?.map((a, i) => (
        <li key={i} className="flex items-start gap-3">
          <div className="mt-1 h-5 w-5 rounded-full bg-slate-100 ring-1 ring-slate-200" />
          <div className="min-w-0 flex-1">
            <div className="text-[13px] text-slate-800 pb-[5px]">{a.text}</div>
            <div className="text-[11px] text-slate-400">{formatTs(a.ts)}</div>
          </div>
        </li>
      ))}
      {!compact && (
        <button className="mt-1 inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
          View full activity <ChevronRight className="size-4" />
        </button>
      )}
    </ul>
  );
}

function Assets({ assets }) {
  return (
    <div className="space-y-3">
      {assets?.map((a, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-xl border border-solid border-slate-200 bg-white p-3"
        >
          <div className="flex min-w-0 items-center gap-3">
            <Monitor className="size-5" />
            <div className="min-w-0">
              <div className="truncate text-sm font-medium">{a.name}</div>
              <div className="truncate text-xs text-slate-500">{a.id}</div>
            </div>
          </div>
          <Badge tone={a.status === "Assigned" ? "blue" : "grey"}>
            {a.status}
          </Badge>
        </div>
      ))}
      <button className="inline-flex items-center gap-1 text-xs text-slate-600 hover:underline">
        Manage assets <ChevronRight className="size-4" />
      </button>
    </div>
  );
}

function Notes() {
  const [notes, setNotes] = useState([
    {
      id: 1,
      text: "Great initiative on latest branding sprint. Keep momentum for Q4.",
      ts: Date.now() - 86400000,
    },
  ]);
  const [text, setText] = useState("");
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-solid border-slate-200 bg-white p-3">
        <textarea
          id="addNotesField"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add a manager note (visible to HR/Managers)…"
          className="min-h-[90px] w-full resize-y rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-sm outline-none focus:ring-2 focus:ring-slate-900/10"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => {
              if (!text.trim()) return;
              setNotes((n) => [{ id: Date.now(), text, ts: Date.now() }, ...n]);
              setText("");
            }}
            className="rounded-xl bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800"
          >
            Save Note
          </button>
        </div>
      </div>
      <ul className="space-y-2">
        {notes?.map((n) => (
          <li
            key={n.id}
            className="rounded-xl border border-solid border-slate-200 bg-white p-3"
          >
            <div className="text-sm text-slate-800">{n.text}</div>
            <div className="text-[11px] text-slate-400">
              {new Date(n.ts).toLocaleString()}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="rounded-xl border border-solid border-slate-200 bg-slate-50 p-3 text-center">
      <div className="text-[11px] text-slate-500">{label}</div>
      <div className="text-sm font-semibold text-slate-800">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function formatTs(ts) {
  try {
    const d =
      typeof ts === "string" ? new Date(ts.replace(" ", "T")) : new Date(ts);
    return d.toLocaleString();
  } catch {
    return String(ts);
  }
}

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
